import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { Table } from 'antd';
import Text from 'antd/es/typography/Text';
import { fileNameEdit } from 'helpers/FileUtils';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/sales_competitors/');

const format = (v) => `${v}%`;

const defaultState = {
  tableData: [],
  lineChartData2: [],
  lineChartData: [],
  total: {},
};

const useInvoicesTickets = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const { handleDownloadPdf, loadingPdf, createCustomEvent } = usePdf();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const summary = useCallback(() => {
    const { total = {} } = data;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  const [columns, setColumns] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
    },
    {
      title: t('Sales Store'),
      dataIndex: 'Sales Store',
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
    {
      title: t('Inc % Sales Store'),
      dataIndex: 'Inc % Sales Store',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Inc % Sales Comp 1'),
      dataIndex: 'Inc % Sales Comp 1',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Inc % Sales Comp 2'),
      dataIndex: 'Inc % Sales Comp 2',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (
          !params.by_period ||
          !params.by_shopping_center ||
          !params.by_company
        )
          return;

        let data, total, error;

        if (!isAllReports) {
          const {
            data: { data: backData, total: backTotal, error: backError = '' },
          } = await api.getFootfalls({ params });
          data = backData;
          total = backTotal;
          error = backError;
        } else {
          const {
            data: backData,
            total: backTotal,
            error: backError = '',
          } = allReportsData;
          data = backData;
          total = backTotal;
          error = backError;
        }

        if (!data) return;

        const lineChartData1 = {
          id: 'Inc % Sales Store',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Inc % Sales Store'],
          })),
        };
        const lineChartData2 = {
          id: 'Inc % Sales Comp 1',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Inc % Sales Comp 1'],
          })),
        };

        const lineChartData3 = {
          id: 'Inc % Sales Comp 2',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Inc % Sales Comp 2'],
          })),
        };

        setData({
          tableData: data,
          lineChartData: [lineChartData1, lineChartData2, lineChartData3],
          total,
          error,
        });
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData(defaultState);
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          createCustomEvent();
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    columns,
    data,
    filters,
    format,
    summary,
    loading,
    handleDownloadExcel,
    title,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesTickets;
